import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5013">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5014">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5015">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5016">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5017" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5018">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5019" />
              <p className="text-sm text-gray-600" data-id="element-5020">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5021">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5022" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5023">
          {/* Quality & Metadata */}
          <div data-id="element-5024">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5025">
              <Brain className="w-3.5 h-3.5" data-id="element-5026" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5027">
              <div className="grid grid-cols-2 gap-4" data-id="element-5028">
                <div data-id="element-5029">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5030">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5031">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5032">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5033">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5034">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5035">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5036">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5037">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5038">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5039">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5040">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5041">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5042">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5043">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5044">
              <Database className="w-3.5 h-3.5" data-id="element-5045" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5046">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5047" />
                <span className="text-sm text-gray-600" data-id="element-5048">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5049">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5050">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5051">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5052">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5053" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5054">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5055">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5056" />
                  <span className="text-sm" data-id="element-5057">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5058">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5059" />
                  <span className="text-sm" data-id="element-5060">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5061">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5062">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5063" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5064">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5065">
                      <div data-id="element-5066">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5067">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5068">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5069">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5070">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5071">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5072">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5073">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5074">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5075">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5076">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5077" />
                        <span className="text-sm text-gray-600" data-id="element-5078">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5079">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5080" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5081" />}
                        <span className="text-sm text-gray-600" data-id="element-5082">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}